
import { useState, useEffect, useMemo } from "react";
import { Report } from "@/types/report";

export type SortDirection = "asc" | "desc";

interface UseWorksheetFilteringProps {
  reports: Report[];
  itemsPerPage: number;
}

export const useWorksheetFiltering = ({ reports, itemsPerPage }: UseWorksheetFilteringProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showOtherSubjectsOnly, setShowOtherSubjectsOnly] = useState(false);
  const [sortField, setSortField] = useState("teaching_date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, showOtherSubjectsOnly, sortField, sortDirection]);

  const normalizeStatus = (status: string | null | undefined): "WAITING" | "APPROVED" | "REJECTED" => {
    if (!status) return "WAITING";
    
    const statusUpper = status.toUpperCase();
    if (statusUpper === "APPROVED") return "APPROVED";
    if (statusUpper === "REJECTED") return "REJECTED";
    return "WAITING";
  };

  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const matchesSearch = !searchQuery || 
        (report.student_full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         report.student_nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         report.teacher_display?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         report.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         report.teaching_content?.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesStatus = statusFilter === "all" || 
        normalizeStatus(report.approved_worksheet_status) === statusFilter;
      
      const matchesOtherSubject = !showOtherSubjectsOnly || report.other_subject === true;
      
      return matchesSearch && matchesStatus && matchesOtherSubject;
    });
  }, [reports, searchQuery, statusFilter, showOtherSubjectsOnly]);
  
  const sortedReports = useMemo(() => {
    return [...filteredReports].sort((a, b) => {
      let valueA, valueB;
      
      // Handle different field types
      switch (sortField) {
        case "teaching_date":
          valueA = new Date(a.teaching_date).getTime();
          valueB = new Date(b.teaching_date).getTime();
          break;
        case "approved_worksheet_status":
          valueA = normalizeStatus(a.approved_worksheet_status);
          valueB = normalizeStatus(b.approved_worksheet_status);
          break;
        default:
          valueA = a[sortField as keyof Report] || '';
          valueB = b[sortField as keyof Report] || '';
      }
      
      // Sort direction
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      // Compare and return
      if (valueA < valueB) return -1 * direction;
      if (valueA > valueB) return 1 * direction;
      return 0;
    });
  }, [filteredReports, sortField, sortDirection]);
  
  const totalPages = Math.ceil(sortedReports.length / itemsPerPage);
  
  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedReports.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedReports, currentPage, itemsPerPage]);
  
  const handleSort = (field: string) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setShowOtherSubjectsOnly(false);
  };

  return {
    paginatedReports,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    showOtherSubjectsOnly,
    setShowOtherSubjectsOnly,
    sortField,
    sortDirection,
    currentPage,
    setCurrentPage,
    totalPages,
    handleSort,
    normalizeStatus,
    resetFilters
  };
};
